import { useEffect, useState } from "react";
import { Save, Send, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import { Panel, PageHeader } from "@/components/Panel";
import { http, formatError } from "@/lib/api";
import { CalendarPanel } from "@/components/SettingsPanels";
import { OmvexisModePanel, ApiKeysPanel, WebsiteBubblePanel } from "@/components/IntegrationPanels";
import { QuotePricingPanel } from "@/components/QuoteDialog";
import { TimezoneSelect, browserTz } from "@/components/TimezoneSelect";

export default function Settings() {
  const [email, setEmail] = useState(null);
  const [cal, setCal] = useState("");
  const [tz, setTz] = useState("UTC");
  const [busy, setBusy] = useState(null);
  useEffect(() => { http.get("/settings").then((r) => { setCal(r.data.calendar_url || ""); setTz(r.data.timezone || "UTC"); setEmail(r.data.team_email || ""); }); }, []);
  if (email === null) return null;

  const saveTz = async () => {
    try { await http.put("/settings", { timezone: tz }); toast.success(`Timezone set to ${tz}`); }
    catch (e) { toast.error(formatError(e)); }
  };

  const save = async () => {
    setBusy("save");
    try { await http.put("/settings", { team_email: email }); toast.success("Team email saved"); }
    catch (e) { toast.error(formatError(e)); }
    setBusy(null);
  };
  const test = async () => {
    setBusy("test");
    try { const { data } = await http.post("/settings/test-email"); toast.success(data.result); }
    catch (e) { toast.error(formatError(e)); }
    setBusy(null);
  };

  return (
    <div>
      <PageHeader code="SYS://CONFIG" title="Settings" sub="Alerts, timezone, calendar, Omvexis safety, website bubble, quote pricing and API keys." />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Panel label="Notification Channel" title="Team Email" testId="settings-email-panel">
          <p className="text-slate-400 text-sm mb-4">The 8am Omvexis briefing and new website quote requests are emailed to this address.</p>
          <input className="hud-input mb-4" type="email" placeholder="team@yourcompany.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="settings-team-email-input" />
          <div className="flex gap-2">
            <button className="hud-btn" onClick={save} disabled={!!busy} data-testid="settings-save-button">{busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
            <button className="hud-btn hud-btn-ghost" onClick={test} disabled={!!busy || !email} data-testid="settings-test-email-button">{busy === "test" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send test email</button>
          </div>
        </Panel>
        <Panel label="Scheduler" title="Daily Auto-Run" testId="settings-schedule-panel">
          <div className="flex items-start gap-3 text-slate-300">
            <Clock size={18} className="text-cyan-300 mt-0.5" />
            <p>Every day at <span className="font-mono-hud text-cyan-200">08:00 your time</span> JARVIS queues payment-due notices for overdue Omvexis shipments (autopilot), emails each overdue buyer a stage-based reminder (polite, then firmer at 7, 15 and 30 days), sends the team the overdue list, and emails an Omvexis briefing including today's meetings to the team. Meetings starting within 90 minutes get an automatic prep note.</p>
          </div>
          <div className="mt-5">
            <div className="hud-label mb-2">Your timezone (agenda, briefings, daily run)</div>
            <div className="flex gap-2">
              <TimezoneSelect value={tz} onChange={setTz} testId="settings-timezone-select" />
              <button className="hud-btn hud-btn-ghost shrink-0" onClick={() => setTz(browserTz())} data-testid="settings-timezone-detect">Detect</button>
              <button className="hud-btn shrink-0" onClick={saveTz} data-testid="settings-timezone-save"><Save size={14} /> Save</button>
            </div>
          </div>
        </Panel>
        <CalendarPanel initial={cal} />
        <OmvexisModePanel />
        <WebsiteBubblePanel />
        <QuotePricingPanel />
        <ApiKeysPanel />
      </div>
    </div>
  );
}
